import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { paymentProviderApi, type PaymentProviderData } from '../../services/paymentProviderApi';

const PROVIDER_TYPE_LABELS: Record<string, string> = {
  vakifbank: 'Vakıfbank',
  iyzico: 'İyzico',
  garanti: 'Garanti',
  paytr: 'PayTR',
  paynkolay: 'Paynkolay',
};

const ENV_LABELS: Record<string, string> = {
  test: 'Test',
  production: 'Canlı',
};

export default function PaymentProviders() {
  const [providers, setProviders] = useState<PaymentProviderData[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchProviders = async () => {
    try {
      setLoading(true);
      const data = await paymentProviderApi.getAll();
      setProviders(data);
    } catch (error) {
      toast.error('POS sağlayıcılar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProviders();
  }, []);

  const handleActivate = async (id: string) => {
    try {
      await paymentProviderApi.activate(id);
      toast.success('POS sağlayıcı aktif edildi');
      fetchProviders();
    } catch (error) {
      toast.error('POS sağlayıcı aktif edilemedi');
    }
  };

  const handleDeactivate = async (id: string, name: string) => {
    if (!window.confirm(`${name} sağlayıcısını pasife almak istediğinize emin misiniz? Başka bir POS aktif edilmedikçe kredi kartı ödemesi alınamaz.`)) return;
    try {
      await paymentProviderApi.deactivate(id);
      toast.success('POS sağlayıcı pasife alındı');
      fetchProviders();
    } catch (error) {
      toast.error('POS sağlayıcı pasife alınamadı');
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`${name} sağlayıcısını silmek istediğinize emin misiniz?`)) return;
    try {
      await paymentProviderApi.remove(id);
      toast.success('POS sağlayıcı silindi');
      fetchProviders();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'POS sağlayıcı silinemedi');
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">POS Yönetimi</h1>
          <p className="text-gray-500 mt-1">Sanal POS sağlayıcılarını yönetin</p>
        </div>
        <Link
          to="/admin/payment-providers/new"
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
        >
          + Yeni POS Ekle
        </Link>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : providers.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-500 mb-4">Henüz POS sağlayıcı tanımlanmamış</p>
          <Link
            to="/admin/payment-providers/new"
            className="text-blue-600 hover:underline"
          >
            İlk POS sağlayıcınızı ekleyin →
          </Link>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600">Sağlayıcı</th>
                <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600">Tip</th>
                <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600">Ortam</th>
                <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600">Taksit</th>
                <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600">Durum</th>
                <th className="text-right px-6 py-3 text-sm font-semibold text-gray-600">İşlemler</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {providers.map((provider) => (
                <tr key={provider.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="font-medium">{provider.name}</div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="bg-gray-100 px-2 py-1 rounded text-sm">
                      {PROVIDER_TYPE_LABELS[provider.type] || provider.type}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded text-sm ${
                      provider.environment === 'production'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {ENV_LABELS[provider.environment]}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {(provider.installmentOptions || []).filter(o => o.isActive).length > 0
                      ? `${(provider.installmentOptions || []).filter(o => o.isActive).length} seçenek`
                      : 'Sadece peşin'}
                  </td>
                  <td className="px-6 py-4">
                    {provider.isActive ? (
                      <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                        Aktif
                      </span>
                    ) : (
                      <span className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-sm">
                        Pasif
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {!provider.isActive && (
                        <button
                          onClick={() => handleActivate(provider.id!)}
                          className="text-green-600 hover:text-green-800 text-sm font-medium"
                        >
                          Aktif Yap
                        </button>
                      )}
                      {provider.isActive && (
                        <button
                          onClick={() => handleDeactivate(provider.id!, provider.name)}
                          className="text-orange-600 hover:text-orange-800 text-sm font-medium"
                        >
                          Pasife Al
                        </button>
                      )}
                      <Link
                        to={`/admin/payment-providers/${provider.id}/edit`}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        Düzenle
                      </Link>
                      {!provider.isActive && (
                        <button
                          onClick={() => handleDelete(provider.id!, provider.name)}
                          className="text-red-600 hover:text-red-800 text-sm font-medium"
                        >
                          Sil
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
